import { Device } from '@capacitor/device';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';

import platformStorage from './platformStorage';

const BIOMETRIC_SERVER = 'com.arbsystems.schoolapp';

export const BIOMETRIC_ENABLED_KEY = 'x-biometric-enabled';

const BIOMETRIC_ERROR_LOCKOUT_PERMANENT = 2;
const BIOMETRIC_ERROR_NOT_ENROLLED = 3;
const BIOMETRIC_ERROR_LOCKOUT = 4;

const BIOMETRY_NONE = 0;
const BIOMETRY_TOUCH_ID = 1;
const BIOMETRY_FACE_ID = 2;
const BIOMETRY_FINGERPRINT = 3;
const BIOMETRY_FACE_AUTHENTICATION = 4;
const BIOMETRY_IRIS_AUTHENTICATION = 5;

export interface StoredDeviceCredentials {
  email: string;
  deviceToken: string;
}

export interface BiometricStatus {
  native: boolean;
  available: boolean;
  type: number;
  errorCode?: number;
}

const isNativePlatform = async (): Promise<boolean> => {
  try {
    const info = await Device.getInfo();
    return info?.platform !== 'web';
  } catch (e) {
    return false;
  }
};

export const getDeviceId = async (): Promise<string> => {
  try {
    const result = await Device.getId();
    return result?.identifier || '';
  } catch (e) {
    return '';
  }
};

export const getBiometricStatus = async (): Promise<BiometricStatus> => {
  if (!(await isNativePlatform())) {
    return { native: false, available: false, type: BIOMETRY_NONE };
  }
  try {
    const result = await NativeBiometric.isAvailable({ useFallback: true });
    return {
      native: true,
      available: Boolean(result?.isAvailable),
      type: Number(result?.biometryType ?? BIOMETRY_NONE),
      errorCode: result?.errorCode,
    };
  } catch (e) {
    return { native: true, available: false, type: BIOMETRY_NONE };
  }
};

export const isBiometricAvailable = async (): Promise<boolean> => {
  const status = await getBiometricStatus();
  return status.available;
};

export const getBiometricLabel = (type: number): string => {
  switch (type) {
    case BIOMETRY_TOUCH_ID:
      return 'Touch ID';
    case BIOMETRY_FACE_ID:
      return 'Face ID';
    case BIOMETRY_FINGERPRINT:
      return 'fingerprint';
    case BIOMETRY_FACE_AUTHENTICATION:
      return 'face unlock';
    case BIOMETRY_IRIS_AUTHENTICATION:
      return 'iris unlock';
    default:
      return 'biometrics';
  }
};

export const getBiometricUnavailableHint = (status: BiometricStatus): string => {
  if (!status.native || status.available) {
    return '';
  }
  if (status.type === BIOMETRY_NONE) {
    return 'This device does not support biometric sign in.';
  }
  if (status.errorCode === BIOMETRIC_ERROR_LOCKOUT || status.errorCode === BIOMETRIC_ERROR_LOCKOUT_PERMANENT) {
    return 'Biometric sign in is temporarily locked. Use your password.';
  }
  if (status.errorCode === BIOMETRIC_ERROR_NOT_ENROLLED) {
    return `Register a ${getBiometricLabel(status.type)} on your device to enable biometric sign in.`;
  }
  return 'Set a screen lock on your device to enable biometric sign in.';
};

export const isBiometricEnabled = async (): Promise<boolean> => {
  if (!(await isBiometricAvailable())) {
    return false;
  }
  return (await platformStorage.get(BIOMETRIC_ENABLED_KEY)) === '1';
};

export const enableBiometric = async (email: string, deviceToken: string): Promise<boolean> => {
  if (!deviceToken || !(await isBiometricAvailable())) {
    return false;
  }
  try {
    await NativeBiometric.setCredentials({ username: email || '', password: deviceToken, server: BIOMETRIC_SERVER });
    await platformStorage.set(BIOMETRIC_ENABLED_KEY, '1');
    return true;
  } catch (e) {
    return false;
  }
};

export const disableBiometric = async (): Promise<void> => {
  await platformStorage.remove(BIOMETRIC_ENABLED_KEY);
  if (!(await isNativePlatform())) {
    return;
  }
  try {
    await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
  } catch (e) {
    return;
  }
};

export const unlockDeviceToken = async (): Promise<StoredDeviceCredentials | null> => {
  if (!(await isBiometricEnabled())) {
    return null;
  }

  try {
    await NativeBiometric.verifyIdentity({
      reason: 'Sign in to Arb',
      title: 'Sign in to Arb',
      useFallback: true,
    });
  } catch (e) {
    return null;
  }

  try {
    const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
    if (!credentials?.password) {
      return null;
    }
    return { email: credentials.username || '', deviceToken: credentials.password };
  } catch (e) {
    return null;
  }
};
